import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./Custom.css";
import App from "./App";
//import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from "react-router-dom";
import { UserProvider } from "./Context/UserContext";
import PracticeProvider from "./Context/PracticeContext";
import { ConfigProvider, theme } from "antd";

// High-fidelity production Light Theme Configuration
const lightTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    // Premium CRM Light Color Palette
    colorPrimary: "#0078D4", // Slate-Blue primary
    colorSuccess: "#059669", // Emerald green
    colorWarning: "#D97706", // Warm Amber
    colorError: "#DC2626", // Premium Red
    colorInfo: "#3B82F6", // Light Info Blue
    colorTextBase: "#1F2937", // Deep slate text
    colorBgBase: "#FFFFFF", // Pure white background

    // Premium Typography
    fontFamily:
      "'Google Sans Flex', 'Google Sans', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: 13,

    // UI Boundaries & Shapes
    borderRadius: 6,
    wireframe: false,
  },
  components: {
    Button: {
      controlHeight: 32,
      controlHeightSmall: 26,
      borderRadius: 8,
    },
    Input: {
      colorBorder: "#E5E7EB",
      activeBorderColor: "#0078D4",
      borderRadius: 6,
    },
    Table: {
      headerBg: "#FFFFFF",
      headerColor: "#4B5563",
      headerSplitColor: "transparent",
      borderColor: "#F3F4F6",
      rowHoverBg: "#EFF6FF",
      padding: 8,
    },
    Menu: {
      itemBg: "transparent",
      itemSelectedBg: "#EFF6FF",
      itemSelectedColor: "#0078D4",
      itemHoverBg: "#F3F4F6",
      itemColor: "#4B5563",
      borderRadius: 6,
    },
  },
};

// High-fidelity production Dark Theme Configuration supporting darkAlgorithm
const darkTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    gradientPrimary: "#0354D7",
    gradientSecondary: "#BAE0FF",
    colorPrimary: "#0354D7",
    colorSuccess: "#52c41a",
    colorWarning: "#ffa266",
    colorError: "#cc363e",
    fontFamily:
      "'Google Sans Flex', 'Google Sans', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    colorTextBase: "#E9EEF9",
    colorBgBase: "#323232",
    colorBgContainer: "#111111",
    colorBgLayout: "#000000",
    colorBgLayoutStrip: "#3A3A3A",
    colorBorder: "#1e2430",
    colorPrimaryBg: "#181818",
    gradientHeaderPrimary: "#323232",
    gradientHeaderSecondary: "#323232",
    bgOpacity: 0.3,
    fontSize: 13,
    borderRadius: 6,
    wireframe: false,
  },
  components: {
    Button: {
      controlHeight: 32,
      controlHeightSmall: 26,
      borderRadius: 6,
    },
    Input: {
      colorBorder: "#1e2430",
      activeBorderColor: "#0354D7",
      borderRadius: 6,
    },
    Table: {
      headerBg: "#181818", // Matches colorPrimaryBg / darker tone
      headerColor: "#E9EEF9",
      headerSplitColor: "transparent",
      borderColor: "#1e2430",
      rowHoverBg: "#323232", // Matches colorBgBase
      padding: 8,
    },
    Menu: {
      itemBg: "transparent",
      itemSelectedBg: "rgba(3, 84, 215, 0.15)",
      itemSelectedColor: "#3B82F6",
      itemHoverBg: "#1c2230",
      itemColor: "#9CA3AF",
      borderRadius: 6,
    },
  },
};

const MainApp: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const isPublicPath = window.location.pathname.startsWith("/data-request");

  useEffect(() => {
    const handleThemeChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      setIsDarkMode(customEvent.detail === "dark");
    };
    window.addEventListener("themeChange", handleThemeChange);

    // Apply global body class for dark mode support in custom CSS
    if (isDarkMode && !isPublicPath) {
      document.body.classList.add("dark-theme");
      document.body.style.backgroundColor = "#000000";
    } else {
      document.body.classList.remove("dark-theme");
      document.body.style.backgroundColor = "#F3F4F6";
    }

    return () => {
      window.removeEventListener("themeChange", handleThemeChange);
    };
  }, [isDarkMode, isPublicPath]);

  return (
    <ConfigProvider
      theme={isPublicPath ? lightTheme : isDarkMode ? darkTheme : lightTheme}
      componentSize="middle"
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ConfigProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);
root.render(
  <UserProvider>
    <PracticeProvider>
      <MainApp />
    </PracticeProvider>
  </UserProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();
